import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { brand, hexToRgb } from './brand';
import { formatCurrency } from './utils';

// Todos los colores y datos de contacto salen de brand.config.js (brand.pdf.colors / brand.contact).
const c = brand.pdf.colors;
const pdfColors = {
  main: hexToRgb(c.main),
  accent: hexToRgb(c.accent),
  tint: hexToRgb(c.tint),
  soft: hexToRgb(c.soft),
  border: hexToRgb(c.border),
  muted: hexToRgb(c.muted),
  text: hexToRgb(brand.colors.text),
  white: [255, 255, 255],
};

const PAGE_W = 210;
const PAGE_H = 297;
const MX = 12;
const CONTENT_W = PAGE_W - MX * 2;
const HEADER_H = 24;
const FOOTER_H = 20; // alto reservado al pie en cada página
const LIMIT_Y = PAGE_H - FOOTER_H;
const DEFAULT_VALIDITY_DAYS = 7;

export const formatQuoteNumber = (n) => `COT-${String(n).padStart(6, '0')}`;

// Dibuja un polígono relleno (puntos absolutos en unidades de la marca) con jsPDF.
function fillPolygon(doc, pts, ox, oy, s) {
  const [first, ...rest] = pts;
  const segs = [];
  let px = first[0];
  let py = first[1];
  rest.forEach(([x, y]) => {
    segs.push([(x - px) * s, (y - py) * s]);
    px = x;
    py = y;
  });
  doc.lines(segs, ox + first[0] * s, oy + first[1] * s, [1, 1], 'F', true);
}

// Dibuja la «M» de la marca (si existe) en blanco con el triángulo interior en el acento.
// Devuelve el ancho dibujado en mm.
function drawMark(doc, x, y, heightMm) {
  const { mark } = brand;
  const s = heightMm / mark.height;
  const xs = mark.shapes.flat().map((p) => p[0]);
  const minX = Math.min(...xs);
  const ox = x - minX * s;
  doc.setFillColor(...pdfColors.accent);
  fillPolygon(doc, mark.accent, ox, y, s);
  doc.setFillColor(...pdfColors.white);
  mark.shapes.forEach((shape) => fillPolygon(doc, shape, ox, y, s));
  return (Math.max(...xs) - minX) * s;
}

function drawHeader(doc, quote) {
  doc.setFillColor(...pdfColors.main);
  doc.rect(0, 0, PAGE_W, HEADER_H, 'F');
  doc.setFillColor(...pdfColors.accent);
  doc.rect(0, HEADER_H, PAGE_W, 1, 'F');

  let textX = MX;
  if (brand.pdf.showMark && brand.mark) textX += drawMark(doc, MX, 5.5, 13) + 4;

  doc.setTextColor(...pdfColors.white);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.text(brand.pdf.title, textX, 12);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  doc.text(brand.pdf.subtitle, textX, 17.5);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.text('COTIZACIÓN', PAGE_W - MX, 12, { align: 'right' });
  if (quote.quote_number) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.text(`N.º ${formatQuoteNumber(quote.quote_number)}`, PAGE_W - MX, 18, { align: 'right' });
  }
}

// Tarjeta con borde fino: etiqueta + líneas ({ text, bold }). Devuelve su alto.
function measureCard(doc, lines, width) {
  doc.setFontSize(8.5);
  let h = 8;
  const rendered = lines.map((l) => {
    doc.setFont('helvetica', l.bold ? 'bold' : 'normal');
    const parts = doc.splitTextToSize(l.text, width - 6);
    h += parts.length * 3.9;
    return { ...l, parts };
  });
  return { h: h + 1.5, rendered };
}

function drawCard(doc, x, y, width, height, label, rendered) {
  doc.setDrawColor(...pdfColors.border);
  doc.setLineWidth(0.25);
  doc.roundedRect(x, y, width, height, 1.2, 1.2, 'S');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7);
  doc.setTextColor(...pdfColors.accent);
  doc.text(label, x + 3, y + 4.2);
  doc.setFontSize(8.5);
  doc.setTextColor(...pdfColors.text);
  let ty = y + 8.4;
  rendered.forEach((l) => {
    doc.setFont('helvetica', l.bold ? 'bold' : 'normal');
    l.parts.forEach((part) => {
      doc.text(part, x + 3, ty);
      ty += 3.9;
    });
  });
}

// Pie en todas las páginas: validez, nota de P., contacto, agradecimiento y paginación.
function drawFooters(doc, validityDays) {
  const total = doc.getNumberOfPages();
  const top = PAGE_H - FOOTER_H + 2;
  const { contact = {} } = brand;
  const entries = [
    ['Consultas', contact.inquiries],
    ['Matriz Quito', contact.quito],
    ['Matriz Quito Norte', contact.quitoNorte],
  ].filter(([, v]) => v);

  for (let page = 1; page <= total; page += 1) {
    doc.setPage(page);
    doc.setDrawColor(...pdfColors.border);
    doc.setLineWidth(0.25);
    doc.line(MX, top, PAGE_W - MX, top);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    doc.setTextColor(...pdfColors.muted);
    doc.text(
      `Esta cotización es válida por ${validityDays} días. Precios sujetos a cambio sin previo aviso.`,
      MX, top + 3.6
    );
    doc.text(`Página ${page} de ${total}`, PAGE_W - MX, top + 3.6, { align: 'right' });
    doc.text('P.: lista de precio aplicada (1 o 2)', MX, top + 7);
    doc.text('Gracias por su preferencia', PAGE_W - MX, top + 7, { align: 'right' });

    // Contacto: máximo 2 líneas, entradas separadas por " | " con la etiqueta en negrita
    const sepW = 3.2; // espacio, barra y espacio
    doc.setFontSize(7.5);
    const widthOf = (txt, bold) => {
      doc.setFont('helvetica', bold ? 'bold' : 'normal');
      return doc.getTextWidth(txt);
    };
    const rows = [[]];
    let rowW = 0;
    entries.forEach((entry) => {
      const w = widthOf(`${entry[0]}: `, true) + widthOf(entry[1], false);
      const add = rows[rows.length - 1].length ? sepW + w : w;
      if (rows[rows.length - 1].length && rowW + add > CONTENT_W) {
        rows.push([entry]);
        rowW = w;
      } else {
        rows[rows.length - 1].push(entry);
        rowW += add;
      }
    });
    rows.slice(0, 2).forEach((row, ri) => {
      let x = MX;
      const y = top + 10.6 + ri * 3.4;
      row.forEach((entry, i) => {
        if (i > 0) {
          doc.setFont('helvetica', 'normal');
          doc.text('|', x + 1.4, y);
          x += sepW;
        }
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...pdfColors.text);
        doc.text(`${entry[0]}: `, x, y);
        x += doc.getTextWidth(`${entry[0]}: `);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(...pdfColors.muted);
        doc.text(entry[1], x, y);
        x += doc.getTextWidth(entry[1]);
      });
    });
  }
}

// Genera el PDF de una cotización/proforma. `quote` usa el mismo formato que el API:
// { created_at, quote_number, user_name, user_code, client_*, notes, items: [{ product_code,
//   product_name, quantity, price_key, unit_price_applied, subtotal }], total_amount }.
// `settings` (opcional): { validity_days }.
export function generateQuotePdf(quote, settings = {}) {
  const doc = new jsPDF();
  const validityDays = settings.validity_days || DEFAULT_VALIDITY_DAYS;
  drawHeader(doc, quote);

  // Tarjetas CLIENTE / DETALLE
  const date = new Date(quote.created_at || Date.now()).toLocaleDateString('es-EC', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
  const clientLines = [];
  if (quote.client_name) clientLines.push({ text: quote.client_name, bold: true });
  if (quote.client_id_number) clientLines.push({ text: `Cédula/RUC: ${quote.client_id_number}` });
  if (quote.client_phone) clientLines.push({ text: `Teléfono: ${quote.client_phone}` });
  if (quote.client_email) clientLines.push({ text: `Correo: ${quote.client_email}` });
  if (quote.client_city) clientLines.push({ text: `Ciudad: ${quote.client_city}` });
  if (quote.client_address) clientLines.push({ text: `Dirección: ${quote.client_address}` });
  if (!clientLines.length) clientLines.push({ text: 'Sin datos de cliente' });
  const detailLines = [
    { text: `Fecha: ${date}` },
    { text: `Vendedor: ${quote.user_name || quote.user_code || 'N/A'}` },
    { text: `Validez: ${validityDays} días` },
  ];
  const cardW = (CONTENT_W - 5) / 2;
  const cardY = HEADER_H + 5;
  const left = measureCard(doc, clientLines, cardW);
  const right = measureCard(doc, detailLines, cardW);
  const cardH = Math.max(left.h, right.h);
  drawCard(doc, MX, cardY, cardW, cardH, 'CLIENTE', left.rendered);
  drawCard(doc, MX + cardW + 5, cardY, cardW, cardH, 'DETALLE', right.rendered);

  // Tabla
  const items = quote.items || [];
  const priceLabel = (key) => (key === 'price_2' ? '2' : key === 'price_1' ? '1' : '-');
  autoTable(doc, {
    startY: cardY + cardH + 4,
    margin: { top: 8, bottom: FOOTER_H + 2, left: MX, right: MX },
    head: [['Código', 'Producto', 'Cant.', 'P.', 'P. Unit.', 'Subtotal']],
    body: items.map((item) => [
      item.product_code,
      item.product_name,
      item.quantity,
      priceLabel(item.price_key),
      formatCurrency(item.unit_price_applied),
      formatCurrency(item.subtotal),
    ]),
    theme: 'plain',
    showHead: 'everyPage',
    rowPageBreak: 'avoid',
    headStyles: {
      fillColor: pdfColors.main,
      textColor: pdfColors.white,
      fontStyle: 'bold',
      fontSize: 8,
      cellPadding: { top: 1.2, bottom: 1.2, left: 1.5, right: 1.5 },
    },
    alternateRowStyles: { fillColor: pdfColors.tint },
    styles: {
      font: 'helvetica',
      fontSize: 8,
      textColor: pdfColors.text,
      cellPadding: { top: 0.8, bottom: 0.8, left: 1.5, right: 1.5 },
      lineWidth: 0,
      overflow: 'linebreak',
    },
    columnStyles: {
      0: { cellWidth: 25 },
      1: { cellWidth: 'auto' },
      2: { cellWidth: 11, halign: 'center' },
      3: { cellWidth: 7, halign: 'center' },
      4: { cellWidth: 17, halign: 'right' },
      5: { cellWidth: 20, halign: 'right' },
    },
    didParseCell: (data) => {
      if (data.section === 'head') {
        const h = ['left', 'left', 'center', 'center', 'right', 'right'][data.column.index];
        data.cell.styles.halign = h;
      }
    },
  });

  // Observaciones (izquierda) y tarjeta de totales (derecha)
  const units = items.reduce((sum, i) => sum + (Number(i.quantity) || 0), 0);
  const totalsW = 72;
  const totalsH = 19;
  const notesW = CONTENT_W - totalsW - 5;
  const lh = 3.8;
  let y = doc.lastAutoTable.finalY + 5;
  if (y + totalsH > LIMIT_Y) {
    doc.addPage();
    y = 12;
  }

  const totalsX = PAGE_W - MX - totalsW;
  doc.setFillColor(...pdfColors.main);
  doc.roundedRect(totalsX, y, totalsW, totalsH, 1.5, 1.5, 'F');
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(...pdfColors.white);
  doc.text(`${items.length} líneas · ${units} unidades`, totalsX + 4, y + 5.5);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(...pdfColors.accent);
  doc.text('TOTAL', totalsX + 4, y + 14);
  doc.setFontSize(15);
  doc.text(formatCurrency(quote.total_amount), totalsX + totalsW - 4, y + 14.5, { align: 'right' });

  const notes = (quote.notes || '').trim();
  if (notes) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    const lines = doc.splitTextToSize(notes, notesW - 8);
    let idx = 0;
    let first = true;
    let cy = y;
    while (idx < lines.length) {
      const head = first ? 6 : 0;
      let room = Math.floor((LIMIT_Y - cy - 4 - head) / lh);
      if (room < 1 || (first && room < Math.min(2, lines.length))) {
        doc.addPage();
        cy = 12;
        first = true;
        room = Math.floor((LIMIT_Y - cy - 4 - 6) / lh);
      }
      const chunk = lines.slice(idx, idx + Math.max(room, 1));
      const boxH = (first ? 6 : 0) + chunk.length * lh + 3;
      doc.setFillColor(...pdfColors.soft);
      doc.rect(MX, cy, notesW, boxH, 'F');
      doc.setFillColor(...pdfColors.accent);
      doc.rect(MX, cy, 1, boxH, 'F');
      let ty = cy + 3.5;
      if (first) {
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(7);
        doc.setTextColor(...pdfColors.accent);
        doc.text('OBSERVACIONES', MX + 4, ty);
        ty += 4.2;
      } else {
        ty += 0.8;
      }
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8.5);
      doc.setTextColor(...pdfColors.text);
      chunk.forEach((l) => {
        doc.text(l, MX + 4, ty);
        ty += lh;
      });
      idx += chunk.length;
      first = false;
      cy += boxH;
      if (idx < lines.length) {
        doc.addPage();
        cy = 12;
      }
    }
  }

  drawFooters(doc, validityDays);
  return doc;
}
